import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';

interface Template {
  template_id: string;
  name: string;
  short_description: string;
  category: string;
  avatar_emoji: string;
  color: string;
  conversation_starters: Array<{ title: string; prompt: string }>;
  product_scopes: string[];
  personality: { tone: string; verbosity: string; proactivity: string };
}

const CATEGORY_COLORS: Record<string, string> = {
  operations: '#3B82F6',
  finance: '#F59E0B',
  technical: '#EF4444',
  'client-facing': '#10B981',
};

export function AgentTemplates() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['agent-templates'],
    queryFn: () => api<{ templates: Template[]; categories: Array<{ id: string; label: string; count: number }> }>('custom-gpts/system-templates'),
  });

  const createFromTemplateMutation = useMutation({
    mutationFn: (templateId: string) => api<{ id: string }>(`custom-gpts/from-template/${templateId}`, { method: 'POST' }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      navigate(`/agents/${data.id}/edit`);
    },
  });

  const templates = data?.templates ?? [];
  const categories = data?.categories ?? [];

  const byCategory = categories.map(cat => ({
    ...cat,
    templates: templates.filter(t => t.category === cat.id),
  }));

  if (isLoading) {
    return (
      <div className="p-8 max-w-6xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3,4,5,6].map(i => <div key={i} className="h-48 bg-muted animate-pulse rounded-lg" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
            <button onClick={() => navigate('/agents')} className="hover:text-foreground">Agents</button>
            <span>/</span>
            <span className="text-foreground">Templates</span>
          </div>
          <h1 className="text-2xl font-bold text-foreground">Agent Templates</h1>
          <p className="text-muted-foreground mt-1">Start from a pre-built MSP agent template and customize it for your team.</p>
        </div>
        <button
          onClick={() => navigate('/agents/new')}
          className="border px-4 py-2 rounded-md text-sm hover:bg-muted font-medium"
        >
          Build from Scratch
        </button>
      </div>

      {byCategory.map(cat => (
        <div key={cat.id} className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-2 h-6 rounded-full" style={{ backgroundColor: CATEGORY_COLORS[cat.id] ?? '#6B7280' }} />
            <h2 className="text-lg font-semibold">{cat.label}</h2>
            <span className="text-sm text-muted-foreground">{cat.count} templates</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {cat.templates.map(template => (
              <div key={template.template_id} className="rounded-xl border bg-card p-5 hover:border-primary/40 transition-colors group">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center text-xl shrink-0"
                    style={{ backgroundColor: `${template.color}20` }}>
                    {template.avatar_emoji}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-foreground">{template.name}</h3>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      {template.product_scopes.slice(0, 3).map(scope => (
                        <span key={scope} className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground uppercase font-medium">
                          {scope}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{template.short_description}</p>
                <div className="flex items-center justify-between">
                  <div className="text-xs text-muted-foreground capitalize">
                    {template.personality.tone} · {template.personality.verbosity}
                  </div>
                  <button
                    onClick={() => createFromTemplateMutation.mutate(template.template_id)}
                    disabled={createFromTemplateMutation.isPending}
                    className="text-sm font-medium text-primary hover:text-primary/80 disabled:opacity-50 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    Use Template →
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
